import { useEffect, useRef, useState } from "react";
import '../components/FormFields.css';
import { ChevronDown } from "lucide-react";

export default function QRDesigner() {
    const canvasRef = useRef(null);
    const [text, setText] = useState("https://jambojar.com/");
    const [size, setSize] = useState(2000);
    const [dotStyle, setDotStyle] = useState("round");
    const [color1, setColor1] = useState("#000000");
    const [transparentBg, setTransparentBg] = useState(false);
    const [bgColor, setBgColor] = useState("#ffffff");
    const [logo, setLogo] = useState(null);
    // NEW STATES
    const [patternStyle, setPatternStyle] = useState("round");
    const [cornerStyle, setCornerStyle] = useState("square");
    function isLightColor(hex) {
        const c = hex.substring(1);
        const rgb = parseInt(c, 16);
        const r = (rgb >> 16) & 255;
        const g = (rgb >> 8) & 255;
        const b = rgb & 255;
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 200;
    }

    useEffect(() => {
        generateQR();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        text,
        size,
        dotStyle,
        color1,
        logo,
        transparentBg,
        bgColor,
        patternStyle,
        cornerStyle,
    ]);

    function generateQR() {
        if (!window.QRCode) return;
        const qr = new window.QRCode(document.createElement("div"), {
            text: text,
            width: 300,
            height: 300,
            correctLevel: window.QRCode.CorrectLevel.H,
        });
        const matrix = qr._oQRCode.modules;
        drawQR(matrix);
    }

    // PATTERN DRAWER
    function drawPattern(ctx, style, x, y, cell) {
        const cx = x + cell / 2;
        const cy = y + cell / 2;
        if (style === "square") {
            ctx.fillRect(x, y, cell, cell);
        } else if (style === "round") {
            ctx.beginPath();
            ctx.arc(cx, cy, cell * 0.42, 0, Math.PI * 2);
            ctx.fill();
        } else if (style === "diamond") {
            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate(Math.PI / 4);
            ctx.fillRect(-cell * 0.35, -cell * 0.35, cell * 0.7, cell * 0.7);
            ctx.restore();
        } else if (style === "dot") {
            ctx.beginPath();
            ctx.arc(cx, cy, cell * 0.3, 0, Math.PI * 2);
            ctx.fill();
        } else if (style === "star") {
            const spikes = 5;
            const step = Math.PI / spikes;
            ctx.beginPath();
            for (let i = 0; i < spikes * 2; i++) {
                const radius = i % 2 === 0 ? cell * 0.45 : cell * 0.2;
                const sx = cx + Math.cos(i * step) * radius;
                const sy = cy + Math.sin(i * step) * radius;
                if (i === 0) ctx.moveTo(sx, sy);
                else ctx.lineTo(sx, sy);
            }
            ctx.closePath();
            ctx.fill();
        }
    }

    // CORNER DRAWER
    function drawCorner(ctx, style, x, y, size) {
        if (style === "square") {
            ctx.strokeRect(x, y, size, size);
            ctx.fillRect(x + size * 0.3, y + size * 0.3, size * 0.4, size * 0.4);
        } else if (style === "round") {
            ctx.beginPath();
            ctx.roundRect(x, y, size, size, size * 0.25);
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(x + size / 2, y + size / 2, size * 0.2, 0, Math.PI * 2);
            ctx.fill();
        } else if (style === "circle") {
            ctx.beginPath();
            ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(x + size / 2, y + size / 2, size * 0.2, 0, Math.PI * 2);
            ctx.fill();
        }
    }

    // OUTER SCANNER BORDER
    function drawScannerBorder(ctx, size, padding, color) {

        const borderLength = size * 0.14;   // corner line length
        const offset = padding * 0.98;      // INCREASED spacing from QR
        const thickness = size * 0.045;      // line thickness

        ctx.strokeStyle = color;
        ctx.lineWidth = thickness;
        ctx.lineCap = "square";

        const left = padding - offset;
        const right = size - padding + offset;
        const top = padding - offset;
        const bottom = size - padding + offset;

        ctx.beginPath();

        // TOP LEFT
        ctx.moveTo(left, top + borderLength);
        ctx.lineTo(left, top);
        ctx.lineTo(left + borderLength, top);

        // TOP RIGHT
        ctx.moveTo(right - borderLength, top);
        ctx.lineTo(right, top);
        ctx.lineTo(right, top + borderLength);

        // BOTTOM RIGHT
        ctx.moveTo(right, bottom - borderLength);
        ctx.lineTo(right, bottom);
        ctx.lineTo(right - borderLength, bottom);

        // BOTTOM LEFT
        ctx.moveTo(left + borderLength, bottom);
        ctx.lineTo(left, bottom);
        ctx.lineTo(left, bottom - borderLength);

        ctx.stroke();
    }

    function drawQR(matrix) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const count = matrix.length;
        canvas.width = size;
        canvas.height = size;
        const padding = size * 0.08;
        const cell = (size - padding * 2) / count;
        let previewBg = bgColor;
        if (isLightColor(color1)) {
            previewBg = "#111111";
        }
        if (!transparentBg) {
            ctx.fillStyle = previewBg;
            ctx.fillRect(0, 0, size, size);
        } else {
            ctx.clearRect(0, 0, size, size);
        }
        ctx.fillStyle = color1;
        ctx.strokeStyle = color1;
        matrix.forEach((row, r) => {
            row.forEach((col, c) => {
                if (!col) return;
                const isFinder =
                    (r < 7 && c < 7) ||
                    (r < 7 && c > count - 8) ||
                    (r > count - 8 && c < 7);
                if (isFinder) return;
                const x = padding + c * cell;
                const y = padding + r * cell;
                drawPattern(ctx, patternStyle, x, y, cell);
            });
        });
        const finderSize = cell * 7;
        ctx.lineWidth = cell * 0.8;
        drawCorner(ctx, cornerStyle, padding, padding, finderSize);
        drawCorner(ctx, cornerStyle, size - finderSize - padding, padding, finderSize);
        drawCorner(ctx, cornerStyle, padding, size - finderSize - padding, finderSize);
        // Draw outer border like scanner frame
        drawScannerBorder(ctx, size, padding, color1);
        if (logo) {
            const img = new Image();
            img.src = logo;
            img.onload = () => {
                const logoSize = size * 0.11;
                const padding = logoSize * 0.25;
                const x = size / 2 - logoSize / 2;
                const y = size / 2 - logoSize / 2;
                ctx.beginPath();
                ctx.arc(size / 2, size / 2, logoSize / 2 + padding, 0, Math.PI * 2);
                ctx.fillStyle = "#ffffff";
                ctx.fill();
                ctx.save();
                ctx.beginPath();
                ctx.arc(size / 2, size / 2, logoSize / 2, 0, Math.PI * 2);
                ctx.clip();
                ctx.drawImage(img, x, y, logoSize, logoSize);
                ctx.restore();
            };
        }
    }
    // function uploadLogo(e) {
    //     const file = e.target.files[0];
    //     if (!file) return;
    //     const reader = new FileReader();
    //     reader.onload = () => {
    //         setLogo(reader.result);
    //     };
    //     reader.readAsDataURL(file);
    // }
    function downloadPNG() {
        const canvas = canvasRef.current;
        const link = document.createElement("a");
        link.download = transparentBg ? "qr-transparent.png" : "qr.png";
        link.href = canvas.toDataURL("image/png");
        link.click();
    }
    const isQrLight = isLightColor(color1);
    return (
        <div className="sm:px-6">
            {/* Header */}
            <div className="text-start">
                <div className="inline-flex items-center gap-3 backdrop-blur-sm rounded-2xl px-0 py-1 border border-white/20 mb-1">
                    <h1 className="text-3xl font-bold text-slate-900">
                        Create dynamic QR codes
                    </h1>
                </div>
                <p className="text-sm text-slate-500">
                    Design, customize, and generate branded QR codes in seconds.
                </p>
            </div>

            <div className="flex flex-col lg:flex-row py-5 gap-10">
                {/* QR Preview */}
                <div className="flex-1 flex items-center justify-center">
                    <div
                        className={`shadow-lg ${isQrLight ? "bg-gray-900" : "bg-white"}`}
                    >
                        <canvas ref={canvasRef} className="w-[400px] h-[400px]" />
                    </div>
                </div>

                {/* Control Panel */}
                <div className="w-full lg:w-80 bg-white rounded-xl shadow-lg p-6 space-y-5 h-full">
                    <h2 className="text-xl font-semibold">Dynamic QR code</h2>

                    <div>
                        <label className="text-sm">URL / Text</label>
                        <input
                            className="mt-1 w-full border rounded p-2"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                        />
                    </div>

                    {/* <div className="form-group">
                        <label className="text-sm">Dot Style</label>
                        <div className='relative'>
                            <select
                                className='appearance-none'
                                value={dotStyle}
                                onChange={(e) => setDotStyle(e.target.value)}
                            >
                                <option value="round">Rounded</option>
                                <option value="square">Square</option>
                            </select>
                            <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
                        </div>
                    </div> */}

                    {/* NEW Pattern Style */}
                    <div className="form-group">
                        <label className="text-sm">Pattern</label>
                        <div className='relative'>
                            <select
                                className='appearance-none'
                                value={patternStyle}
                                onChange={(e) => setPatternStyle(e.target.value)}
                            >
                                <option value="round">Rounded</option>
                                <option value="square">Square</option>
                                <option value="diamond">Diamond</option>
                                <option value="dot">Dot</option>
                                <option value="star">Star</option>
                            </select>
                            <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
                        </div>
                    </div>

                    {/* NEW Corner Style */}
                    <div className="form-group">
                        <label className="text-sm">Corner Style</label>
                        <div className='relative'>
                            <select
                                className='appearance-none'
                                value={cornerStyle}
                                onChange={(e) => setCornerStyle(e.target.value)}
                            >
                                <option value="square">Square</option>
                                <option value="round">Rounded</option>
                                <option value="circle">Circle</option>
                            </select>
                            <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm">Code Color</label>
                        <input
                            type="color"
                            value={color1}
                            onChange={(e) => setColor1(e.target.value)}
                            className="w-full h-10"
                        />
                    </div>
                    {/* <div>
                    
                    <label className="text-sm">Gradient End</label>
                    <input
                        type="color"
                        value={color2}
                        onChange={(e) => setColor2(e.target.value)}
                        className="w-full h-10"
                    />
                </div> */}
                    <div>
                        <label className="text-sm">Background Color</label>
                        <input
                            type="color"
                            value={bgColor}
                            onChange={(e) => setBgColor(e.target.value)}
                            className="w-full h-10"
                        />
                    </div>
                    {/* <div> <label className="text-sm">Upload Logo</label> <input type="file" onChange={uploadLogo} className="mt-1" /> </div> */}
                    {/* <div> <label className="text-sm">Size</label> <input type="range" min="400" max="2000" value={size} onChange={(e) => setSize(e.target.value)} className="w-full" /> </div> */}
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="transparent-bg"
                            checked={transparentBg}
                            onChange={(e) => setTransparentBg(e.target.checked)}
                        />
                        <label className="text-sm" htmlFor="transparent-bg">

                            Transparent Background
                        </label>
                    </div>
                    <button
                        onClick={downloadPNG}
                        className="w-full bg-black text-white py-2 rounded hover:bg-black"
                    >
                        Download PNG
                    </button>
                </div>
            </div>
        </div >
    );
}
